// Next scheduled backup time — derived from the real backup cron.
//
// The scheduled-backup job runs HOURLY and backs up the tenants whose
// `backup_hour_utc` matches the current UTC hour:
//   services/api/src/backup/backup.service.ts
//   @Cron('0 0 * * * *', { timeZone: 'UTC' })
//   SELECT ... WHERE status = 'ACTIVE' AND backup_hour_utc = $1
//
// It briefly ran daily at 2:00 AM IST for every ACTIVE tenant, which ignored
// the owner's configured hour entirely. This module follows the column, so a
// tenant that moves its backup window sees the change here.
//
// The column is explicitly UTC — that is what its name says and what the API
// documents — so the hour is a UTC hour and only the DISPLAY is on the
// business's clock (T28; IST when unknown).

import { DEFAULT_TIMEZONE, getTimezone } from '@bbpos/types';

const DAY_MS = 24 * 60 * 60 * 1000;

/** The tenants table default, for a page that has not loaded the setting. */
export const DEFAULT_BACKUP_HOUR_UTC = 2;

/** The next occurrence of `hourUtc`:00 UTC strictly after `now`. */
export function nextScheduledBackup(
  hourUtc: number = DEFAULT_BACKUP_HOUR_UTC,
  now: Date = new Date(),
): Date {
  const target = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    hourUtc,
    0,
    0,
  );
  return new Date(target <= now.getTime() ? target + DAY_MS : target);
}

/**
 * Formats the next scheduled backup on the business's wall clock with its zone
 * label, e.g. "20 Jul, 07:30 IST" or "20 Jul, 06:00 GST".
 */
export function formatNextScheduledBackup(
  hourUtc: number = DEFAULT_BACKUP_HOUR_UTC,
  now: Date = new Date(),
  timeZone: string = DEFAULT_TIMEZONE,
): string {
  const next = nextScheduledBackup(hourUtc, now);
  const zone = getTimezone(timeZone);
  const formatted = next.toLocaleString('en-IN', {
    timeZone: zone.iana,
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `${formatted} ${zone.abbr}`;
}
